import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CASE_TYPES } from "@/lib/utils-domain";
import { CheckCircle, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BRAND_NAME } from "@/lib/brand";

const INTAKE_BG = "https://images.pexels.com/photos/7679173/pexels-photo-7679173.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Intake() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", case_type: "Personal Injury", matter_description: "" });
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/intake", form);
            setSubmitted(true);
        } catch {
            toast.error("Could not submit. Please try again.");
        } finally { setBusy(false); }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white p-6">
                <div className="max-w-lg text-center animate-fade-up">
                    <CheckCircle size={48} weight="duotone" className="text-emerald-600 mx-auto" />
                    <h1 className="font-display text-4xl font-extrabold mt-5 tracking-tight">Thank you.</h1>
                    <p className="text-slate-600 mt-3">Your intake has been received. A member of our team will reach out within one business day to schedule a consultation.</p>
                    <Link to="/" className="btn-secondary mt-8 inline-block" data-testid="intake-back-home">Back to home</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="relative hidden lg:block">
                <img src={INTAKE_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B2447]/95 via-[#0B2447]/80 to-transparent" />
                <div className="relative z-10 h-full flex flex-col p-12 text-white">
                    <Link to="/" className="font-display text-3xl font-extrabold tracking-tight">{BRAND_NAME}</Link>
                    <div className="mt-auto max-w-md">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-200 mb-4">Client Intake</div>
                        <h2 className="font-display text-4xl font-bold leading-tight">Tell us about your matter.</h2>
                        <p className="mt-4 text-slate-200/90 leading-relaxed">
                            Your information is confidential. Submitting this form does not create
                            an attorney-client relationship — we will reach out to discuss.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <form onSubmit={submit} className="w-full max-w-md animate-fade-up" data-testid="intake-form">
                    <Link to="/" className="lg:hidden font-display text-2xl font-extrabold block mb-6 tracking-tight">{BRAND_NAME}</Link>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">New client intake</div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Get a consultation.</h1>
                    <p className="text-sm text-slate-600 mt-2 mb-8">No obligation. Replies within one business day.</p>

                    <div className="space-y-4">
                        <div>
                            <label className="label-sharp">Full name</label>
                            <input required value={form.name} onChange={set("name")} className="input-sharp" data-testid="intake-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-sharp">Email</label>
                                <input required type="email" value={form.email} onChange={set("email")} className="input-sharp" data-testid="intake-email-input" />
                            </div>
                            <div>
                                <label className="label-sharp">Phone</label>
                                <input value={form.phone} onChange={set("phone")} className="input-sharp" data-testid="intake-phone-input" />
                            </div>
                        </div>
                        <div>
                            <label className="label-sharp">Matter type</label>
                            <select value={form.case_type} onChange={set("case_type")} className="input-sharp" data-testid="intake-type-select">
                                {CASE_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sharp">Tell us about your matter</label>
                            <textarea required rows={5} value={form.matter_description} onChange={set("matter_description")} className="input-sharp" data-testid="intake-description-input" />
                        </div>
                    </div>
                    <button type="submit" disabled={busy} className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="intake-submit-button">
                        <Scales size={16} weight="duotone" /> {busy ? "Submitting…" : "Submit intake"}
                    </button>
                    <div className="text-[11px] font-mono text-slate-400 text-center mt-4">
                        We treat your information with strict confidentiality.
                    </div>
                </form>
            </div>
        </div>
    );
}
